import { createClient } from '@supabase/supabase-js'

// 從環境變量中獲取Supabase配置
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL

// 支援新的API key格式並保持向後兼容性
const getSupabaseKey = () => {
  // 優先使用新的publishable key格式
  const publishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY
  if (publishableKey) {
    return publishableKey
  }

  // 如果沒有新格式，使用舊的anon key (向後兼容)
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
  if (anonKey) {
    console.warn('🚨 您正在使用舊的 anon key 格式。請考慮升級到新的 publishable key 格式。詳情: https://github.com/orgs/supabase/discussions/29260')
    return anonKey
  }

  return null
}

const supabaseKey = getSupabaseKey()

// 創建Supabase客戶端
export const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null

// 檢查Supabase配置是否正確
export const checkSupabaseConfig = () => {
  if (!supabaseUrl) {
    return {
      isValid: false,
      error: '缺少 REACT_APP_SUPABASE_URL 環境變量',
      suggestion: '請在 .env 文件中設置您的 Supabase 項目 URL'
    }
  }

  if (!supabaseKey) {
    return {
      isValid: false,
      error: '缺少 Supabase API Key',
      suggestion: '請在 .env 文件中設置 REACT_APP_SUPABASE_PUBLISHABLE_KEY (新格式) 或 REACT_APP_SUPABASE_ANON_KEY (舊格式)'
    }
  }

  // 檢查是否使用了範例值
  if (supabaseUrl.includes('your-project-id') ||
    supabaseKey.includes('your-key-here') ||
    supabaseKey.includes('your-anon-key')) {
    return {
      isValid: false,
      error: '使用的是範例配置值',
      suggestion: '請將範例值替換為您的實際 Supabase 項目配置'
    }
  }

  return {
    isValid: true,
    keyType: supabaseKey.startsWith('sb_publishable_') ? 'publishable' : 'anon',
    error: null,
    suggestion: null
  }
}

// 導出用於水井資訊的數據庫操作函數
export const wellService = {
  // 獲取純井資料（不合併施工紀錄）
  async getWellsOnly() {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('*')

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('獲取水井資訊失敗:', error)
      throw error
    }
  },

  // 獲取所有水井資訊
  async getAllWells() {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('*')

      if (error) throw error

      return data || []
    } catch (error) {
      console.error('獲取水井資訊失敗:', error)
      throw error  // 重新拋出錯誤，讓WellList組件知道出錯了
    }
  },

  // 搜索水井資訊 (支援名稱、位置搜索)
  async searchWells(searchTerm) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('*')
        .or(`name.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`)
      // 移除排序，因為CSV資料沒有created_at欄位

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('搜索水井資訊失敗:', error)
      throw error
    }
  },

  // 按狀態篩選水井
  async getWellsByStatus(status) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('*')
        .eq('status', status)
      // 移除排序，因為CSV資料沒有created_at欄位

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('按狀態篩選水井失敗:', error)
      throw error
    }
  },

  // 按位置篩選水井
  async getWellsByLocation(location) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('*')
        .ilike('location', `%${location}%`)
      // 移除排序，因為CSV資料沒有created_at欄位

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('按位置篩選水井失敗:', error)
      throw error
    }
  },

  // 複合查詢 (支援多個條件同時篩選)
  async searchWellsWithFilters(filters) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      let query = supabase
        .from('well')
        .select('*')

      // 名稱或位置搜索
      if (filters.searchTerm) {
        query = query.or(`name.ilike.%${filters.searchTerm}%,location.ilike.%${filters.searchTerm}%`)
      }

      // 狀態篩選
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status)
      }

      // 位置篩選
      if (filters.location) {
        query = query.ilike('location', `%${filters.location}%`)
      }

      // 深度範圍篩選
      if (filters.minDepth) {
        query = query.gte('depth', filters.minDepth)
      }
      if (filters.maxDepth) {
        query = query.lte('depth', filters.maxDepth)
      }

      // 水質篩選
      if (filters.waterQuality) {
        query = query.eq('water_quality', filters.waterQuality)
      }

      // 日期範圍篩選 - 移除，因為CSV資料沒有created_at欄位
      // if (filters.startDate) {
      //   query = query.gte('created_at', filters.startDate)
      // }
      // if (filters.endDate) {
      //   query = query.lte('created_at', filters.endDate)
      // }

      const { data, error } = await query
      // 移除排序，因為CSV資料沒有created_at欄位

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('複合查詢失敗:', error)
      throw error
    }
  },

  // 獲取統計資訊
  async getWellStatistics() {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('status, water_quality, depth')

      if (error) throw error

      const stats = {
        total: data.length,
        byStatus: {},
        byWaterQuality: {},
        averageDepth: 0,
        maxDepth: 0,
        minDepth: Number.MAX_VALUE
      }

      data.forEach(well => {
        // 統計狀態
        if (well.status) {
          stats.byStatus[well.status] = (stats.byStatus[well.status] || 0) + 1
        }

        // 統計水質
        if (well.water_quality) {
          stats.byWaterQuality[well.water_quality] = (stats.byWaterQuality[well.water_quality] || 0) + 1
        }

        // 統計深度
        if (well.depth) {
          const depth = parseFloat(well.depth)
          if (!isNaN(depth)) {
            stats.averageDepth += depth
            stats.maxDepth = Math.max(stats.maxDepth, depth)
            stats.minDepth = Math.min(stats.minDepth, depth)
          }
        }
      })

      stats.averageDepth = stats.averageDepth / data.length
      if (stats.minDepth === Number.MAX_VALUE) stats.minDepth = 0

      return stats
    } catch (error) {
      console.error('獲取統計資訊失敗:', error)
      return null
    }
  },

  // 根據ID獲取特定水井資訊
  async getWellById(id) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('well')
        .select('*')
        .eq('id', id)
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data
    } catch (error) {
      console.error('獲取水井資訊失敗:', error)
      return null
    }
  },

  // 新增水井資訊
  async createWell(wellData) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      // 處理空字符串轉換為 null，避免資料庫錯誤
      const processedData = { ...wellData }

      // 對所有欄位進行空字符串檢查，將空字符串轉換為 null
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === '') {
          processedData[key] = null
        }
      })

      // 移除 id 欄位，讓資料庫自動生成主鍵
      if (processedData.id !== undefined) {
        delete processedData.id
      }

      console.log('提交的水井資料:', processedData) // 調試用

      const { data, error } = await supabase
        .from('well')
        .insert([processedData])
        .select()

      if (error) throw error
      return data[0]
    } catch (error) {
      console.error('新增水井資訊失敗:', error)
      throw error
    }
  },

  // 更新水井資訊
  async updateWell(id, wellData) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      // 處理空字符串轉換為 null，避免資料庫錯誤
      const processedData = { ...wellData }

      // 對所有欄位進行空字符串檢查，將空字符串轉換為 null
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === '') {
          processedData[key] = null
        }
      })

      const { data, error } = await supabase
        .from('well')
        .update(processedData)
        .eq('id', id)
        .select()

      if (error) throw error
      return data[0]
    } catch (error) {
      console.error('更新水井資訊失敗:', error)
      throw error
    }
  },

  // 刪除水井資訊
  async deleteWell(id) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      // 1. 先刪除關聯的施工紀錄
      const { error: vendorError } = await supabase
        .from('vendor_well')
        .delete()
        .eq('well_id', id)

      if (vendorError) throw vendorError

      // 2. 再刪除水井本身
      const { error } = await supabase
        .from('well')
        .delete()
        .eq('id', id)

      if (error) throw error
      return true
    } catch (error) {
      console.error('刪除水井資訊失敗:', error)
      throw error
    }
  }
}

// 施工紀錄服務 - 針對 vendor_well 表
export const vendorWellService = {
  // 新增施工紀錄
  async createConstructionRecord(recordData) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      // 處理空字符串轉換為 null，避免 bigint 轉換錯誤
      const processedData = { ...recordData }

      // 對所有欄位進行空字符串檢查，將空字符串轉換為 null
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === '') {
          processedData[key] = null
        }
      })

      // 移除 id 欄位，讓資料庫自動生成主鍵
      if (processedData.id !== undefined) {
        delete processedData.id
      }

      console.log('提交的資料:', processedData) // 調試用，可以看到實際提交的資料

      const { data, error } = await supabase
        .from('vendor_well')
        .insert([processedData])
        .select()

      if (error) throw error

      // 同步資料至 well 資料表
      if (processedData.well_id) {
        const fieldsToSync = [
          'hp', 'motor_type', 'pump_depth', 'well_tube_material',
          'static_water_level(m)', 'outlet_pipe_diameter(inch)', 'outlet_pipe_material',
          'standard_hydraulic_head_x_standard_water_volume', 'vendor', 'repair_date',
          'failure_reason', 'e_num', 'unit'
        ]

        const updatePayload = {}
        fieldsToSync.forEach(field => {
          if (processedData[field] !== undefined && processedData[field] !== null) {
            updatePayload[field] = processedData[field]
          }
        })

        // 處理特殊欄位對映
        if (processedData['installation_depth_of_water_level_observation_pipe(m)'] !== undefined &&
          processedData['installation_depth_of_water_level_observation_pipe(m)'] !== null) {
          updatePayload['installation_depth_of water_level_observation_pipe(m)'] = processedData['installation_depth_of_water_level_observation_pipe(m)']
        }

        if (Object.keys(updatePayload).length > 0) {
          const { error: syncError } = await supabase
            .from('well')
            .update(updatePayload)
            .eq('id', processedData.well_id)

          if (syncError) {
            console.error('同步至水井資料失敗:', syncError)
            // 不拋出錯誤，避免阻擋原本的新增流程
          }
        }
      }

      return data[0]
    } catch (error) {
      console.error('新增施工紀錄失敗:', error)
      throw error
    }
  },

  // 獲取所有施工紀錄
  async getAllConstructionRecords() {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('vendor_well')
        .select('*')

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('獲取施工紀錄失敗:', error)
      throw error
    }
  },

  // 根據電號搜索施工紀錄
  async getConstructionRecordsByENum(eNum) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { data, error } = await supabase
        .from('vendor_well')
        .select('*')
        .eq('e_num', eNum)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('搜索施工紀錄失敗:', error)
      throw error
    }
  },

  // 更新施工紀錄
  async updateConstructionRecord(id, recordData) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      // 確保將空字串同樣轉為 null 以正確處理
      const processedData = { ...recordData }
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === '') {
          processedData[key] = null
        }
      })

      const { data, error } = await supabase
        .from('vendor_well')
        .update(processedData)
        .eq('id', id)
        .select()

      if (error) throw error
      const updatedRecord = data[0]

      // 同步更新至 well 資料表
      if (updatedRecord && updatedRecord.well_id) {
        // 先取得所有該水井的施工紀錄中，repair_date 最新的那筆，確認目前這筆是否為最新
        const { data: latestRecords } = await supabase
          .from('vendor_well')
          .select('id')
          .eq('well_id', updatedRecord.well_id)
          .order('repair_date', { ascending: false })
          .limit(1)

        // 只有當這筆更新的紀錄是最新的一筆時，才同步到 well
        if (latestRecords && latestRecords.length > 0 && latestRecords[0].id === id) {
          const fieldsToSync = [
            'hp', 'motor_type', 'pump_depth', 'well_tube_material',
            'static_water_level(m)', 'outlet_pipe_diameter(inch)', 'outlet_pipe_material',
            'standard_hydraulic_head_x_standard_water_volume', 'vendor', 'repair_date',
            'failure_reason', 'e_num', 'unit'
          ]

          const updatePayload = {}
          fieldsToSync.forEach(field => {
            if (updatedRecord[field] !== undefined && updatedRecord[field] !== null) {
              updatePayload[field] = updatedRecord[field]
            }
          })

          if (updatedRecord['installation_depth_of_water_level_observation_pipe(m)'] !== undefined &&
            updatedRecord['installation_depth_of_water_level_observation_pipe(m)'] !== null) {
            updatePayload['installation_depth_of water_level_observation_pipe(m)'] = updatedRecord['installation_depth_of_water_level_observation_pipe(m)']
          }

          if (Object.keys(updatePayload).length > 0) {
            const { error: syncError } = await supabase
              .from('well')
              .update(updatePayload)
              .eq('id', updatedRecord.well_id)

            if (syncError) {
              console.error('同步至水井資料失敗:', syncError)
            }
          }
        }
      }

      return updatedRecord
    } catch (error) {
      console.error('更新施工紀錄失敗:', error)
      throw error
    }
  },

  // 刪除施工紀錄
  async deleteConstructionRecord(id) {
    if (!supabase) {
      throw new Error('Supabase 客戶端未正確初始化')
    }

    try {
      const { error } = await supabase
        .from('vendor_well')
        .delete()
        .eq('id', id)

      if (error) throw error
      return true
    } catch (error) {
      console.error('刪除施工紀錄失敗:', error)
      throw error
    }
  }
} 